// Exceeds target ceiling — single responsibility justified (issue CRUD with notifications and side effects)
import { supabase } from '../lib/supabase'
import { fromPence } from '../utils/currency'
import { getCurrentUserId, verifyIssueAccess, verifyIssueCreateAccess } from '../lib/auth'
import { applyPropertyFilter } from '../lib/portfolioFilter'
import { logIssueCreated, logIssueStatusChange } from '../lib/audit'
import { createExpense } from './financials'
import { handleScheduledMaintenanceIssueCompleted } from './scheduledMaintenance'
import { autoCancelVisits } from './maintenanceVisits'
import type { Issue, IssueInsert, IssueUpdate } from '../types/maintenance'

const ISSUE_SELECT = `
  *,
  property:properties(id, name, address_line1, address_line2, city, postcode),
  unit:units(id, name, floor),
  asset:assets(id, name),
  reporter:profiles!issues_reported_by_fkey(full_name, email, phone)
`

export async function getIssues(filters?: {
  propertyId?: string
  propertyIds?: string[]
  status?: string
  urgency?: string
  userId?: string
}): Promise<Issue[]> {
  const userId = filters?.userId ?? await getCurrentUserId()

  // Get issues for properties owned by the current user (exclude soft-deleted)
  let query = supabase
    .from('issues')
    .select(`${ISSUE_SELECT}, prop_owner:properties!inner(owner_id)`)
    .eq('prop_owner.owner_id', userId)
    .is('deleted_at', null)
    .order('reported_at', { ascending: false })

  query = applyPropertyFilter(query, filters?.propertyIds)
  if (filters?.propertyId) query = query.eq('property_id', filters.propertyId)
  if (filters?.status) query = query.eq('status', filters.status)
  if (filters?.urgency) query = query.eq('urgency', filters.urgency)

  const { data, error } = await query
  if (error) throw error
  return data as Issue[]
}

export async function getIssuesByReporter(): Promise<Issue[]> {
  const userId = await getCurrentUserId()
  const { data, error } = await supabase
    .from('issues')
    .select(ISSUE_SELECT)
    .or(`reported_by.eq.${userId},access_contact_id.eq.${userId}`)
    .is('deleted_at', null)
    .order('reported_at', { ascending: false })

  if (error) throw error
  return data as Issue[]
}

export async function getIssuesByContractor(): Promise<Issue[]> {
  const userId = await getCurrentUserId()

  // Fetch the contractor's approved_contractor record IDs so we can find
  // jobs assigned via the approved_contractors path (pending invite flow).
  const { data: acRecords } = await supabase
    .from('approved_contractors')
    .select('id')
    .eq('contractor_id', userId)

  const acIds = (acRecords || []).map(r => r.id)

  // Query issues assigned either directly (assigned_to) or via
  // approved_contractors (assigned_approved_contractor_id)
  const orFilter = acIds.length > 0
    ? `assigned_to.eq.${userId},assigned_approved_contractor_id.in.(${acIds.join(',')})`
    : `assigned_to.eq.${userId}`

  const { data, error } = await supabase
    .from('issues')
    .select(ISSUE_SELECT)
    .or(orFilter)
    .is('deleted_at', null)
    .order('reported_at', { ascending: false })

  if (error) throw error
  return data as Issue[]
}

export async function getIssuesByApprovedContractor(approvedContractorId: string): Promise<Issue[]> {
  const userId = await getCurrentUserId()

  // Verify the caller is either the contractor themselves or the owner of the
  // properties these issues belong to. First check if the approved_contractor
  // record belongs to/is associated with the current user.
  const { data: contractor } = await supabase
    .from('approved_contractors')
    .select('id, contractor_id, owner_id')
    .eq('id', approvedContractorId)
    .single()

  if (!contractor) throw new Error('Approved contractor not found')
  if (contractor.contractor_id !== userId && contractor.owner_id !== userId) {
    throw new Error('Access denied')
  }

  const { data, error } = await supabase
    .from('issues')
    .select(ISSUE_SELECT)
    .eq('assigned_approved_contractor_id', approvedContractorId)
    .is('deleted_at', null)
    .order('reported_at', { ascending: false })

  if (error) throw error
  return data as Issue[]
}

export async function getIssue(id: string): Promise<Issue> {
  const userId = await getCurrentUserId()
  await verifyIssueAccess(id, userId)

  const { data, error } = await supabase
    .from('issues')
    .select(ISSUE_SELECT)
    .eq('id', id)
    .single()

  if (error) throw error
  return data as Issue
}

export async function createIssue(input: IssueInsert): Promise<Issue> {
  const userId = await verifyIssueCreateAccess(input.property_id)

  // Resolve access contact: if the creator is a tenant, they're the access contact.
  // Otherwise, resolve the primary active tenant for the property.
  let accessContactId: string | null = null
  const { data: creatorProfile } = await supabase
    .from('profiles')
    .select('role')
    .eq('id', userId)
    .single()

  if (creatorProfile?.role === 'tenant') {
    accessContactId = userId
  } else {
    const { data: resolved } = await supabase.rpc('resolve_access_contact', {
      p_property_id: input.property_id,
    })
    accessContactId = resolved || null
  }

  const { data, error } = await supabase
    .from('issues')
    .insert({
      ...input,
      reported_by: userId,
      access_contact_id: accessContactId,
      status: 'reported',
      reported_at: new Date().toISOString(),
    })
    .select(ISSUE_SELECT)
    .single()

  if (error) throw error

  // Audit log: issue created
  await logIssueCreated(data.id, {
    property_id: input.property_id,
    title: input.title,
    category: input.category,
    urgency: input.urgency,
    reported_by: userId
  })

  // No notification dispatch here. `trg_issue_created` on this INSERT
  // emits to properties.owner_id (migration 397), skipping an owner who
  // reported the job themselves — which the browser did too, by passing
  // `userId` as actorId.

  return data as Issue
}

export async function updateIssue(id: string, updates: IssueUpdate): Promise<Issue> {
  const userId = await getCurrentUserId()
  await verifyIssueAccess(id, userId)

  // Get current issue state for audit logging, notifications, and expense creation
  const { data: currentIssue } = await supabase
    .from('issues')
    .select('status, title, property_id, assigned_to, quoted_amount_pence, final_cost_pence, reference_number, reported_by')
    .eq('id', id)
    .single()

  const { data, error } = await supabase
    .from('issues')
    .update(updates)
    .eq('id', id)
    .select(ISSUE_SELECT)
    .single()

  if (error) throw error

  // Return immediately — the remaining side effects (audit, visit
  // cancellation, schedule advancement) run in the background so the UI
  // isn't blocked. Notifications are emitted by the database; see
  // dispatchPostUpdateSideEffects.
  void dispatchPostUpdateSideEffects(id, updates, currentIssue)

  return data as Issue
}

/**
 * Handles all post-update side effects asynchronously: audit logging,
 * visit cancellation, and scheduled-maintenance advancement. Runs
 * fire-and-forget so updateIssue() returns as fast as possible. Each
 * side effect has its own error handling.
 *
 * NOTIFICATIONS ARE NOT HERE. Every notification about an issue row is
 * emitted by `notify_issue_change()` (migration 384), which reads the
 * same transition from the row itself and derives each recipient from
 * properties.owner_id / reported_by / assigned_to / the portfolio
 * clients. Two exceptions still dispatch from the client because they
 * carry free text no column holds: `rejectQuote`'s note and
 * `raiseConcern`'s concern.
 */
async function dispatchPostUpdateSideEffects(
  id: string,
  updates: IssueUpdate,
  currentIssue: { status: string; assigned_to: string | null } | null
): Promise<void> {
  try {
    // Audit log: status change (fire-and-forget within this block)
    if (updates.status && currentIssue?.status !== updates.status) {
      logIssueStatusChange(id, currentIssue?.status || 'unknown', updates.status, updates)
        .catch(() => { /* Audit logging should never break the flow */ })
    }

    // Auto-cancel pending visits from the old contractor on reassignment
    if (updates.assigned_to && updates.assigned_to !== currentIssue?.assigned_to && currentIssue?.assigned_to) {
      autoCancelVisits(id, 'Contractor reassigned')
        .catch(() => { /* Non-blocking */ })
    }

    if (updates.status && currentIssue?.status !== updates.status) {
      if (updates.status === 'closed') {
        autoCancelVisits(id, 'Issue closed')
          .catch(() => { /* Non-blocking */ })

        // If this issue was auto-created from a scheduled maintenance task,
        // advance the schedule and notify the owner.
        handleScheduledMaintenanceIssueCompleted(id)
          .catch(() => { /* Non-blocking */ })
      }

      if (updates.status === 'cancelled') {
        autoCancelVisits(id, 'Issue cancelled')
          .catch(() => { /* Non-blocking */ })
      }
    }
  } catch (err) {
    console.error('Post-update side effects failed:', err)
  }
}

/**
 * Chase a contractor who hasn't responded to a stalled issue.
 * Sends notification + email to the contractor.
 */
export async function chaseContractor(issueId: string): Promise<void> {
  // chase_contractor (migration 385) verifies the caller against
  // get_my_property_ids() and writes the notification itself — chasing
  // changes no row, so there is no transition for a trigger to read.
  const { error } = await supabase.rpc('chase_contractor', { p_issue_id: issueId })
  if (error) throw new Error(error.message)
}

/**
 * Returns the minimal issue fields needed to compute sidebar badge counts.
 * Only fetches status, assigned_to, and assigned_approved_contractor_id —
 * no joins to units, rooms, assets, or profiles — keeping the payload small.
 * Used by LandlordLayout instead of the full getIssues() call.
 */
export async function getIssuesBadgeData(userIdOverride?: string): Promise<
  Pick<Issue, 'status' | 'assigned_to' | 'assigned_approved_contractor_id'>[]
> {
  const userId = userIdOverride ?? await getCurrentUserId()

  const { data, error } = await supabase
    .from('issues')
    .select('status, assigned_to, assigned_approved_contractor_id, prop_owner:properties!inner(owner_id)')
    .eq('prop_owner.owner_id', userId)
    .is('deleted_at', null)
    .is('archived_at', null)
    .not('status', 'in', '("closed","cancelled")')

  if (error) throw error
  return (data ?? []) as Pick<Issue, 'status' | 'assigned_to' | 'assigned_approved_contractor_id'>[]
}

/**
 * Close an issue with explicit payment confirmation (payment gate).
 * If payment data is provided, creates the expense + financial transaction
 * and notifies the contractor. If null, closes without expense (no-cost job).
 */
export async function closeIssueWithPayment(
  issueId: string,
  payment: { amountPence: number; paidAt: string; notes?: string } | null
): Promise<Issue> {
  const now = new Date().toISOString()

  const updates: IssueUpdate = {
    status: 'closed' as const,
    closed_at: now,
    payment_status: payment ? 'paid' : 'no_cost',
    paid_at: payment ? payment.paidAt : null,
  }

  if (payment) {
    updates.final_cost_pence = payment.amountPence
  }

  const result = await updateIssue(issueId, updates)

  // Create expense and financial transaction only when payment is confirmed
  if (payment && payment.amountPence > 0) {
    void createPaymentRecords(issueId, result, payment.amountPence, payment.paidAt)
  }

  return result
}

/**
 * Creates expense + financial transaction records for a paid issue.
 * Runs fire-and-forget after closeIssueWithPayment returns.
 */
async function createPaymentRecords(
  issueId: string,
  issue: Issue,
  amountPence: number,
  paidAt: string
): Promise<void> {
  const expenseDate = paidAt.split('T')[0]
  const description = `${issue.title} (${issue.reference_number})`

  // Look for invoice document
  let invoicePath: string | null = null
  let invoiceName: string | null = null
  try {
    const { data: invoiceDocs } = await supabase
      .from('issue_files')
      .select('storage_path, original_name')
      .eq('issue_id', issueId)
      .eq('file_kind', 'document')
      .eq('category', 'invoice')
      .order('created_at', { ascending: false })
      .limit(1)

    if (invoiceDocs && invoiceDocs.length > 0) {
      // The bucket is recorded as a value now, not as an `issue-documents:`
      // prefix on the path (migration 480).
      invoicePath = invoiceDocs[0].storage_path
      invoiceName = invoiceDocs[0].original_name
    }
  } catch {
    // Non-blocking
  }

  // Create expense record (auto-links a financial_transactions row via createExpense)
  try {
    await createExpense({
      property_id: issue.property_id,
      tenancy_id: null,
      category: 'repairs_maintenance',
      custom_category_id: null,
      description,
      amount: fromPence(amountPence),
      expense_date: expenseDate,
      receipt_storage_path: invoicePath,
      receipt_bucket: invoicePath ? 'issue-documents' : null,
      receipt_name: invoiceName,
      tax_year: null,
      is_deductible: true,
      linked_issue_id: issueId,
      created_by: ''
    }, {
      contractor_id: issue.assigned_to || undefined,
      source: 'maintenance_auto',
      source_ref: `issue_${issueId}`,
    })
  } catch (expenseError) {
    console.error('Failed to create expense for issue payment:', expenseError)
  }

  // The contractor's `invoice_paid` is emitted by notify_issue_change()
  // (migration 388) off the `payment_status -> paid` transition that
  // closeIssueWithPayment above has already made.
}

export async function getOpenIssueCountsByProperty(): Promise<Record<string, number>> {
  const userId = await getCurrentUserId()

  const { data, error } = await supabase
    .from('issues')
    .select('property_id, prop_owner:properties!inner(owner_id)')
    .eq('prop_owner.owner_id', userId)
    .is('deleted_at', null)
    .is('archived_at', null)
    .not('status', 'in', '("closed","cancelled")')

  if (error) throw error

  const counts: Record<string, number> = {}
  for (const issue of data || []) {
    counts[issue.property_id] = (counts[issue.property_id] || 0) + 1
  }
  return counts
}
